/**
 * Dinheiro no sistema e SEMPRE bigint em centavos. Nunca float, nunca number.
 *
 * Por que bigint e nao number: number em JS e IEEE-754 e 0.1 + 0.2 !== 0.3.
 * Centavos inteiros eliminam isso. bigint (e nao Int32) porque int4 estoura em
 * R$ 21.474.836,47, e patrimonio com imovel chega perto.
 *
 * Por que string no JSON: JSON.stringify(1n) lanca TypeError. Toda saida de API
 * serializa centavos como string decimal ("123456"), e o cliente reconverte.
 */
/** Maior valor aceito: R$ 999.999.999.999,99. Acima disso e erro de digitacao. */
export const MAX_CENTS = 99999999999999n;
export const MIN_CENTS = -99999999999999n;
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
function assertInRange(value) {
    if (value > MAX_CENTS || value < MIN_CENTS) {
        throw new MoneyError(`Valor monetario fora da faixa suportada: ${value}`);
    }
    return value;
}
/**
 * Converte texto digitado por humano em centavos.
 * Aceita "1.234,56", "1234,56", "1234.56", "R$ 1.234,56", "-12,34", "1234".
 *
 * Regra de desambiguacao: o ULTIMO separador com 1 ou 2 digitos a direita e o
 * decimal; os demais sao de milhar. "1.234" e mil duzentos e trinta e quatro
 * reais, porque tem 3 casas depois do ponto.
 */
export function parseMoney(input) {
    if (typeof input === 'bigint')
        return assertInRange(input);
    if (typeof input === 'number') {
        if (!Number.isFinite(input))
            throw new MoneyError('Valor monetario nao finito');
        // number entra so por conveniencia de teste; converte via string para nao
        // arrastar o erro binario do float.
        return parseMoney(input.toFixed(2));
    }
    const raw = input.trim();
    if (raw === '')
        throw new MoneyError('Valor monetario vazio');
    const cleaned = raw.replace(/\s/g, '').replace(/^R\$/i, '');
    const negative = cleaned.startsWith('-') || /^\(.*\)$/.test(cleaned);
    const digitsOnly = cleaned.replace(/[()\-+]/g, '');
    if (!/^[\d.,]+$/.test(digitsOnly)) {
        throw new MoneyError(`Valor monetario invalido: ${input}`);
    }
    const sepIndex = Math.max(digitsOnly.lastIndexOf(','), digitsOnly.lastIndexOf('.'));
    let intPart;
    let fracPart;
    if (sepIndex === -1) {
        intPart = digitsOnly;
        fracPart = '';
    }
    else {
        const tail = digitsOnly.slice(sepIndex + 1);
        if (/^\d{1,2}$/.test(tail)) {
            intPart = digitsOnly.slice(0, sepIndex);
            fracPart = tail;
        }
        else {
            // separador de milhar: nao ha parte decimal
            intPart = digitsOnly;
            fracPart = '';
        }
    }
    // A parte inteira so pode estar limpa ou agrupada de 3 em 3 por um unico
    // separador. Sem isso, "12,34,56" seria lido como 1.234,56 em vez de erro.
    if (!isValidIntegerPart(intPart)) {
        throw new MoneyError(`Valor monetario invalido: ${input}`);
    }
    intPart = intPart.replace(/[.,]/g, '');
    if (intPart === '')
        intPart = '0';
    const frac = (fracPart + '00').slice(0, 2);
    const cents = BigInt(intPart) * 100n + BigInt(frac);
    return assertInRange(negative ? -cents : cents);
}
function isValidIntegerPart(value) {
    if (value === '')
        return true;
    if (/^\d+$/.test(value))
        return true;
    const hasDot = value.includes('.');
    const hasComma = value.includes(',');
    // Milhar com dois separadores diferentes na mesma parte inteira e lixo.
    if (hasDot && hasComma)
        return false;
    const separator = hasDot ? '\\.' : ',';
    return new RegExp(`^\\d{1,3}(${separator}\\d{3})+$`).test(value);
}
/** Serializa para a borda da API. bigint nao sobrevive ao JSON.stringify. */
export function centsToString(cents) {
    return cents.toString();
}
/** Le da borda da API. Exige inteiro puro: nao aceita "12,34" aqui, de proposito. */
export function centsFromString(value) {
    if (!/^-?\d+$/.test(value))
        throw new MoneyError(`Centavos invalidos: ${value}`);
    return assertInRange(BigInt(value));
}
/** 123456n -> "1.234,56" (sem simbolo). */
export function formatCents(cents) {
    const negative = cents < 0n;
    const absolute = negative ? -cents : cents;
    const intPart = absolute / 100n;
    const frac = (absolute % 100n).toString().padStart(2, '0');
    const grouped = intPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negative ? '-' : ''}${grouped},${frac}`;
}
/** 123456n -> "R$ 1.234,56". Usar SOMENTE na camada de apresentacao. */
export function formatBRL(cents, options) {
    const value = options?.signDisplay === 'never' && cents < 0n ? -cents : cents;
    return `${value < 0n ? '-' : ''}R$ ${formatCents(value < 0n ? -value : value)}`;
}
export const add = (a, b) => assertInRange(a + b);
export const sub = (a, b) => assertInRange(a - b);
export const neg = (a) => -a;
export const abs = (a) => (a < 0n ? -a : a);
export const sum = (values) => assertInRange(values.reduce((acc, v) => acc + v, 0n));
/** Divisao inteira com arredondamento half-up, correta tambem para negativos. */
export function divideRoundHalfUp(numerator, denominator) {
    if (denominator === 0n)
        throw new MoneyError('Divisao por zero');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const remainder = n % d;
    const rounded = remainder * 2n >= d ? quotient + 1n : quotient;
    return assertInRange(negative ? -rounded : rounded);
}
/**
 * Multiplica por uma taxa decimal ("0.0175", "1.5") com arredondamento half-up
 * no ultimo centavo, que e a convencao usada em contratos no Brasil.
 */
export function multiplyByRate(cents, rate) {
    const rateStr = typeof rate === 'number' ? rate.toString() : rate.trim();
    if (!/^-?\d+(\.\d+)?$/.test(rateStr))
        throw new MoneyError(`Taxa invalida: ${rate}`);
    const negative = rateStr.startsWith('-');
    const [intPart = '0', fracPart = ''] = rateStr.replace('-', '').split('.');
    const scale = 10n ** BigInt(fracPart.length);
    const numerator = BigInt(intPart + fracPart) * (negative ? -1n : 1n);
    return divideRoundHalfUp(cents * numerator, scale);
}
/**
 * Divide um valor em n partes sem perder nem inventar centavo.
 * allocate(10000n, 3) -> [3334n, 3333n, 3333n]. A soma bate SEMPRE.
 * Usado em compras parceladas e no rateio de split.
 */
export function allocate(total, parts) {
    if (!Number.isInteger(parts) || parts <= 0) {
        throw new MoneyError(`Numero de partes invalido: ${parts}`);
    }
    const n = BigInt(parts);
    const negative = total < 0n;
    const value = negative ? -total : total;
    const base = value / n;
    const remainder = Number(value % n);
    const result = [];
    for (let i = 0; i < parts; i += 1) {
        const share = i < remainder ? base + 1n : base;
        result.push(negative ? -share : share);
    }
    return result;
}
/** Rateio proporcional a pesos, preservando a soma exata (split de transacao). */
export function allocateByWeights(total, weights) {
    if (weights.length === 0)
        throw new MoneyError('Pesos vazios');
    if (weights.some((w) => !Number.isFinite(w) || w < 0))
        throw new MoneyError('Peso invalido');
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    if (totalWeight <= 0)
        throw new MoneyError('Soma dos pesos deve ser positiva');
    const negative = total < 0n;
    const value = negative ? -total : total;
    const scale = 1000000;
    const scaled = weights.map((w) => BigInt(Math.round((w / totalWeight) * scale)));
    const scaledTotal = scaled.reduce((a, b) => a + b, 0n) || 1n;
    const raw = scaled.map((w) => (value * w) / scaledTotal);
    let leftover = value - raw.reduce((a, b) => a + b, 0n);
    const result = raw.slice();
    let i = 0;
    while (leftover > 0n) {
        const idx = i % result.length;
        result[idx] = result[idx] + 1n;
        leftover -= 1n;
        i += 1;
    }
    return result.map((v) => (negative ? -v : v));
}
//# sourceMappingURL=index.js.map